import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, PartyPopper } from "lucide-react";

// Festival start: 17 September of current or next year (whichever is upcoming)
// Festival ENDS: Saturday Sept 19 at 20:00 (8 PM) - festival is currently running
function getTargetDate() {
  const now = new Date();
  const year = now.getFullYear();
  let target = new Date(year, 8, 19, 20, 0, 0); // Sept 19 (Saturday) at 8 PM
  if (target.getTime() < now.getTime()) {
    target = new Date(year + 1, 8, 19, 20, 0, 0);
  }
  return target;
}

function diffParts(target) {
  const total = Math.max(0, target.getTime() - Date.now());
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((total / (1000 * 60)) % 60);
  const seconds = Math.floor((total / 1000) % 60);
  return { total, days, hours, minutes, seconds };
}

export default function CountdownBar() {
  const [target] = useState(getTargetDate);
  const [t, setT] = useState(() => diffParts(target));

  useEffect(() => {
    const id = setInterval(() => setT(diffParts(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  const live = t.total > 0;

  return (
    <div
      className="relative z-[60] bg-gradient-to-l from-[#0066CC] via-[#0a4a99] to-[#1E293B] text-white overflow-hidden"
      data-testid="countdown-bar"
    >
      {/* subtle animated shine */}
      <motion.div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,214,0,0.25),transparent_40%)]"
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container-x relative flex flex-row items-center justify-between gap-2 py-1.5 sm:py-3">
        <div className="hidden sm:flex items-center gap-2 text-xs sm:text-sm font-black flex-shrink-0">
          {live ? (
            <>
              <CalendarClock className="w-4 h-4 text-[#FFD600]" />
              <span>ينتهي المهرجان يوم السبت الساعة 8 ليلًا — بعد</span>
            </>
          ) : (
            <>
              <PartyPopper className="w-4 h-4 text-[#FFD600]" />
              <span>انتهى المهرجان — شكرًا لحضوركم!</span>
            </>
          )}
        </div>

        {/* Mobile-only compact label */}
        <div className="sm:hidden flex items-center gap-1.5 text-[10px] font-black flex-shrink-0">
          <CalendarClock className="w-3.5 h-3.5 text-[#FFD600]" />
          <span className="text-[#FFD600]">{live ? "ينتهي بعد" : "انتهى"}</span>
        </div>

        {live && (
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0" data-testid="countdown-values" dir="ltr">
            <TimeBlock value={t.days} label="يوم" testid="days" />
            <Sep />
            <TimeBlock value={t.hours} label="ساعة" testid="hours" />
            <Sep />
            <TimeBlock value={t.minutes} label="دقيقة" testid="minutes" />
            <Sep />
            <TimeBlock value={t.seconds} label="ثانية" testid="seconds" />
          </div>
        )}

        <div className="hidden md:flex items-center gap-1.5 text-xs font-black text-[#FFD600] flex-shrink-0">
          17 • 18 • 19 سبتمبر • 5-8م
        </div>
      </div>
    </div>
  );
}

function TimeBlock({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center min-w-[36px] sm:min-w-[54px]" data-testid={`countdown-${testid}`}>
      <div className="relative w-full h-6 sm:h-9 rounded-md sm:rounded-lg bg-white/10 border border-white/15 backdrop-blur-sm overflow-hidden flex items-center justify-center">
        <motion.span
          key={value}
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="font-display text-sm sm:text-xl font-black text-[#FFD600] tabular-nums"
        >
          {String(value).padStart(2, "0")}
        </motion.span>
      </div>
      <span className="text-[8px] sm:text-[10px] font-bold text-white/70 mt-0.5">{label}</span>
    </div>
  );
}

function Sep() {
  return <span className="text-[#FFD600] font-black text-sm sm:text-xl -mt-2 sm:-mt-3 select-none">:</span>;
}
